import { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { SmartImage } from "@/components/SmartImage";
import { TREATMENT, TreatmentBadge } from "@/components/Badges";

/**
 * Before/after slider built for speed.
 * - The divider position lives in a CSS variable (--pos = width of the BEFORE layer, 0..100) written straight to the DOM
 *   inside requestAnimationFrame - no React state, no re-render, no layout: the before layer is clipped with clip-path.
 * - Pointer capture so a drag continues outside the box; with a mouse the divider simply follows the pointer (hover-scrub).
 * - `sweep`: one reveal (100 -> initial) when scrolled into view.
 * - Imperative API for choreography (the landing stage): `jump(p)`, `animate(to, ms, ease)` -> Promise<boolean> (false when
 *   the visitor took over mid-way), `get()`. `onTakeover` fires the first time the visitor interacts.
 * - `smart` renders the layers with <SmartImage> (retries / expired-link recovery) for signed app media.
 */
const EASE_OUT = (t) => 1 - Math.pow(2, -10 * t);
const EASE_IN_OUT = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const EASE_IN_OUT_QUART = (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2);
/* film-like reveal: alive within the first frames (no dead start), steady through the middle, soft landing */
const EASE_IN_OUT_SINE = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
/* glide: a soft quadratic ramp-in (25% of the time), a calm constant-speed cruise, then a long quadratic landing (35%).
   Velocity is continuous, never rushes, and the line is visibly moving within the first ~400 ms of a 4.6 s sweep. */
const glide = (a, b) => {
  const v = 1 / (1 - a / 2 - b / 2);
  return (t) => (t < a ? (v * t * t) / (2 * a) : t > 1 - b ? 1 - (v * (1 - t) * (1 - t)) / (2 * b) : (v * a) / 2 + v * (t - a));
};
const EASE_GLIDE = glide(0.25, 0.35);
const EASES = { out: EASE_OUT, inout: EASE_IN_OUT, quart: EASE_IN_OUT_QUART, sine: EASE_IN_OUT_SINE, glide: EASE_GLIDE, linear: (t) => t };
const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

export const Compare = forwardRef(function Compare({
  before, after, beforeFallbacks, afterFallbacks, beforeLabel = "Før", afterLabel = null, kind = null, initial = 50, aspect = "4 / 3", fit = "cover",
  sweep = false, hoverScrub = true, interactive = true, smart = false, priority = false, rounded = "rounded-[12px]", className, testid = "compare", onTakeover, onRelease, children,
}, outerRef) {
  const root = useRef(null);
  const pos = useRef(sweep ? 100 : initial);
  const target = useRef(pos.current);
  const raf = useRef(0);
  const tween = useRef(0);
  const resolveTween = useRef(null);
  const dragging = useRef(false);
  const took = useRef(false);

  const write = useCallback((p) => {
    pos.current = p;
    const el = root.current;
    if (!el) return;
    el.style.setProperty("--pos", `${p}%`);
    el.setAttribute("aria-valuenow", String(Math.round(p)));
  }, []);

  // coalesce pointer events into one DOM write per frame
  const schedule = useCallback((p) => {
    target.current = clamp(p);
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => { raf.current = 0; write(target.current); });
  }, [write]);

  const stopTween = useCallback((completed = false) => {
    if (tween.current) cancelAnimationFrame(tween.current);
    tween.current = 0;
    const r = resolveTween.current; resolveTween.current = null;
    r?.(completed);
  }, []);

  const animate = useCallback((to, dur, ease = "out") => new Promise((resolve) => {
    stopTween(false);
    resolveTween.current = resolve;
    const fn = typeof ease === "function" ? ease : (EASES[ease] || EASE_OUT);
    const from = pos.current; const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / Math.max(1, dur));
      write(from + (to - from) * fn(t));
      if (t < 1) tween.current = requestAnimationFrame(tick); else { tween.current = 0; stopTween(true); }
    };
    tween.current = requestAnimationFrame(tick);
  }), [stopTween, write]);

  const takeOver = useCallback(() => {
    stopTween(false);
    if (!took.current) { took.current = true; }
    onTakeover?.();
  }, [stopTween, onTakeover]);

  // initial position + optional sweep on first view
  useEffect(() => {
    write(pos.current);
    const el = root.current;
    if (!el || !sweep) return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let fired = false;
    const io = new IntersectionObserver((es) => {
      if (fired || !es.some((e) => e.isIntersecting)) return;
      fired = true; io.disconnect();
      if (reduced || took.current) { write(initial); return; }
      animate(initial, 2200, "out");
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); stopTween(false); if (raf.current) cancelAnimationFrame(raf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [before, after]);

  useImperativeHandle(outerRef, () => ({
    jump: (p) => { stopTween(false); write(clamp(p)); },
    animate: (to, ms, ease) => animate(clamp(to), ms, ease),
    get: () => pos.current,
    stop: () => stopTween(false),
  }), [animate, stopTween, write]);

  const pct = (clientX) => { const r = root.current.getBoundingClientRect(); return ((clientX - r.left) / r.width) * 100; };
  const onPointerDown = (e) => { dragging.current = true; root.current?.setPointerCapture?.(e.pointerId); takeOver(); schedule(pct(e.clientX)); };
  const onPointerMove = (e) => {
    if (dragging.current) { schedule(pct(e.clientX)); return; }
    if (hoverScrub && e.pointerType === "mouse") { takeOver(); schedule(pct(e.clientX)); }
  };
  // release = the user let go (drag end) or the mouse left the frame while hover-scrubbing: the owner may resume autoplay
  const onPointerUp = (e) => {
    const was = dragging.current;
    dragging.current = false; root.current?.releasePointerCapture?.(e.pointerId);
    if (was) onRelease?.(pos.current);
  };
  const onPointerLeave = (e) => { if (!dragging.current && hoverScrub && e.pointerType === "mouse" && took.current) onRelease?.(pos.current); };
  const onKeyDown = (e) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault(); takeOver();
    const next = e.key === "Home" ? 0 : e.key === "End" ? 100 : pos.current + (e.key === "ArrowLeft" ? -4 : 4);
    animate(clamp(next), 160, "out");
  };

  const imgCls = fit === "contain" ? "object-contain" : "object-cover";
  const Layer = ({ src, fallbacks, alt, className: c }) => (smart
    ? <SmartImage src={src} fallbacks={fallbacks} alt={alt} className={cn("absolute inset-0", c)} imgClassName={imgCls} loading={priority ? "eager" : "lazy"} />
    : <img src={src} alt={alt} className={cn("absolute inset-0 h-full w-full", imgCls, c)} draggable={false} loading={priority ? "eager" : "lazy"} decoding="async" fetchPriority={priority ? "high" : undefined} />);

  return (
    <div ref={root} className={cn("cmp relative w-full overflow-hidden select-none bg-[hsl(var(--tint))]", interactive ? "touch-none cursor-ew-resize" : "pointer-events-none", rounded, className)} style={{ aspectRatio: aspect, "--pos": `${pos.current}%` }}
      {...(interactive
        ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onPointerLeave, onKeyDown, role: "slider", tabIndex: 0, "aria-label": "Sammenlign før og etter", "aria-valuemin": 0, "aria-valuemax": 100 }
        : { role: "img", "aria-label": `Før og etter: ${beforeLabel} og ${afterLabel || (kind ? TREATMENT[kind]?.label : "etter")}` })}
      aria-valuenow={Math.round(pos.current)} data-testid={testid} data-interactive={interactive ? "true" : "false"}>
      {after && <Layer src={after} fallbacks={afterFallbacks} alt={afterLabel || "Etter"} />}
      {before && <div className="cmp-before absolute inset-0"><Layer src={before} fallbacks={beforeFallbacks} alt={beforeLabel} /></div>}
      {after && before && (
        <div className="cmp-line absolute inset-y-0 pointer-events-none" aria-hidden="true">
          <div className="h-full w-0.5 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.12)]" />
          {interactive ? (
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center text-[hsl(var(--ink))]">
              <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-hidden="true"><path d="M5 1 1 5l4 4M11 1l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
          ) : (
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-white shadow-md" />
          )}
        </div>
      )}
      <span className="cmp-label-before absolute top-3 left-3 rounded-[8px] bg-black/55 text-white px-2.5 py-1 text-[12px] font-medium backdrop-blur pointer-events-none">{beforeLabel}</span>
      <span className="cmp-label-after absolute top-3 right-3 flex items-center gap-1.5 pointer-events-none">
        {kind && <TreatmentBadge kind={kind} />}
        {afterLabel && !kind && <span className="rounded-[8px] bg-white/90 text-[hsl(var(--ink))] px-2.5 py-1 text-[12px] font-medium backdrop-blur">{afterLabel}</span>}
      </span>
      {children}
    </div>
  );
});
